"use strict";
import { BINANCE_API_BASE, clampBinanceSlippage, getBinanceChainId } from "features/binance/constants";
import { quoteRequestTimeoutSignal } from "features/routing/api/quoteRequestTimeout";
const BINANCE_OK_CODE = 0;
function buildBaseQuery(params) {
  const binanceChainId = getBinanceChainId(params.chainId);
  if (!binanceChainId) {
    throw new Error(`Binance is not supported on chain ${params.chainId}`);
  }
  return new URLSearchParams({
    binanceChainId,
    amount: params.amount,
    fromTokenAddress: params.fromTokenAddress,
    toTokenAddress: params.toTokenAddress
  });
}
async function fetchBinance(path, query) {
  const url = `${BINANCE_API_BASE}?path=${encodeURIComponent(path)}&${query.toString()}`;
  const res = await fetch(url, { signal: quoteRequestTimeoutSignal() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const reason = body?.msg || body?.error || body?.message || body?.reason || res.statusText;
    throw new Error(`Binance ${path} error: ${reason}`);
  }
  if (body?.code !== void 0 && body.code !== BINANCE_OK_CODE) {
    throw new Error(`Binance ${path} error: ${body?.msg || body?.error || body.code}`);
  }
  if (body?.data === void 0 || body.data === null) {
    throw new Error(`Binance ${path} error: empty response`);
  }
  return body;
}
function normalizeDexRouterList(list) {
  if (!list) {
    return [];
  }
  return list.map((hop) => ({
    dexName: hop.dexProtocol?.dexName ?? "Unknown",
    percent: hop.dexProtocol?.percent ?? "0",
    fromTokenSymbol: hop.fromToken?.tokenSymbol,
    toTokenSymbol: hop.toToken?.tokenSymbol,
    fromTokenAddress: hop.fromToken?.tokenContractAddress,
    toTokenAddress: hop.toToken?.tokenContractAddress
  })).filter((hop) => hop.dexName !== "Unknown" || hop.percent !== "0");
}
function normalizeQuote(raw) {
  return {
    quoteId: raw.quoteId,
    fromTokenAmount: raw.fromTokenAmount,
    toTokenAmount: raw.toTokenAmount,
    priceImpactPercent: raw.priceImpactPercent,
    estimateGasFee: raw.estimateGasFee,
    approveTarget: raw.approveTarget,
    feeAmount: raw.feeAmount,
    feeToken: raw.feeToken,
    tradeFee: raw.tradeFee,
    dexRouterList: normalizeDexRouterList(raw.dexRouterList),
    vendorName: raw.vendorName
  };
}
export async function fetchBinanceQuote(params) {
  const query = buildBaseQuery(params);
  const envelope = await fetchBinance("api/v1/dex/aggregator/quote", query);
  const quotes = envelope.data;
  if (!Array.isArray(quotes) || quotes.length === 0) {
    throw new Error("Binance quote error: no routes returned");
  }
  const best = quotes.find((q) => q.isBest) ?? quotes[0];
  return normalizeQuote(best);
}
export async function fetchBinanceSwap(params) {
  if (!params.account) {
    throw new Error("Binance /swap requires an account address");
  }
  const quote = await fetchBinanceQuote(params);
  const slippagePercent = clampBinanceSlippage(params.slippagePercent ?? 0.5);
  const query = buildBaseQuery(params);
  query.set("userWalletAddress", params.account);
  query.set("quoteId", quote.quoteId);
  query.set("slippagePercent", slippagePercent.toString());
  const envelope = await fetchBinance("api/v1/dex/aggregator/swap", query);
  const swap = envelope.data;
  if (!swap?.tx?.to || !swap.tx.data) {
    throw new Error("Binance swap error: route is not executable as a standard transaction");
  }
  const routerResult = swap.routerResult;
  const swapDexRouterList = normalizeDexRouterList(routerResult?.dexRouterList);
  const merged = {
    quoteId: quote.quoteId,
    fromTokenAmount: routerResult?.fromTokenAmount ?? quote.fromTokenAmount,
    toTokenAmount: routerResult?.toTokenAmount ?? quote.toTokenAmount,
    priceImpactPercent: routerResult?.priceImpactPercent ?? quote.priceImpactPercent,
    estimateGasFee: routerResult?.estimateGasFee ?? quote.estimateGasFee,
    approveTarget: routerResult?.approveTarget ?? quote.approveTarget,
    feeAmount: routerResult?.feeAmount ?? quote.feeAmount,
    feeToken: routerResult?.feeToken ?? quote.feeToken,
    tradeFee: routerResult?.tradeFee ?? quote.tradeFee,
    dexRouterList: swapDexRouterList.length ? swapDexRouterList : quote.dexRouterList,
    vendorName: routerResult?.vendorName ?? quote.vendorName,
    to: swap.tx.to,
    data: swap.tx.data,
    value: swap.tx.value || "0",
    gas: swap.tx.gas,
    gasPrice: swap.tx.gasPrice,
    minReceiveAmount: swap.tx.minReceiveAmount
  };
  return merged;
}
